// Safari's MediaController fires internal errors when restoring a
// backgrounded tab whose video element has stale buffered ranges:
//
//   ReferenceError: Can't find variable: EmptyRanges
//   played@, buffered@, endTimeForBufferedRangeContainingCurrentTime@,
//   syncControl@, handleEvent@
//
// They're cosmetic — the page underneath is fully functional and the user
// can press play again. CRA's react-error-overlay catches them in dev and
// shows a scary modal anyway. This guard swallows just those specific
// messages so the overlay no longer pops; everything else still surfaces.

const NOISE_PATTERN = /\b(EmptyRanges|endTimeForBufferedRangeContainingCurrentTime|syncControl|handleEvent)\b/;

const isBenignSafariMediaError = (msg) => {
  if (!msg) return false;
  return NOISE_PATTERN.test(msg);
};

export const installSafariMediaErrorFilter = () => {
  if (typeof window === 'undefined') return;

  // Capture-phase listener — beats addEventListener handlers that bubble.
  window.addEventListener('error', (event) => {
    const msg = event?.message || event?.error?.message || '';
    if (isBenignSafariMediaError(msg)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);
  window.addEventListener('unhandledrejection', (event) => {
    const msg = event?.reason?.message || String(event?.reason || '');
    if (isBenignSafariMediaError(msg)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);

  // CRA's react-error-overlay attaches via the legacy `window.onerror`
  // property, which is independent of addEventListener and not affected by
  // stopImmediatePropagation. Wrap the property with a getter/setter so
  // anything assigning to `window.onerror` later is chained through our
  // filter — returning true tells the browser the error is handled and
  // suppresses the overlay.
  let chained = window.onerror;
  const filteredOnError = function(msg, source, lineno, colno, error) {
    if (isBenignSafariMediaError(String(msg)) || isBenignSafariMediaError(error?.message)) {
      return true;
    }
    if (typeof chained === 'function') {
      return chained.call(window, msg, source, lineno, colno, error);
    }
    return false;
  };
  try {
    Object.defineProperty(window, 'onerror', {
      configurable: true,
      get() { return filteredOnError; },
      set(v) { chained = v; },
    });
  } catch (e) { /* property locked; capture listener still helps */ }
};
